import { useEffect, useRef, useState } from 'react';

import type { LoadingStateType } from '@/shared/config';
import {
  renderFullHeadMPRView,
  type FullHeadMPRSavedView,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { debug } from '@/shared/lib';

import {
  FULL_HEAD_MPR_SAVED_VIEW_PREVIEW_SIDE_PX,
  type FullHeadMprSliceGroupView,
  type FullHeadMprSliceRecordViewsByTileID,
  type FullHeadMprSliceTileView,
} from '../config';
import {
  getFullHeadMprSavedViewTiles,
  pixelsToPngUrl,
  putFullHeadMprSavedViewTiles,
} from '../lib';

const makeRasterKey = ({
  toothID,
  view,
  thicknessMm,
}: {
  toothID: string;
  view: FullHeadMPRSavedView;
  thicknessMm?: number;
}) => `${toothID}:${thicknessMm ?? 'saved'}:${JSON.stringify(view)}`;

export const useFullHeadMprReportTiles = ({
  groups,
  isEnabled = true,
  recordsByTileID,
  toothID,
}: {
  groups: FullHeadMprSliceGroupView[];
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  toothID: string;
  isEnabled?: boolean;
}) => {
  const seriesTilesByID = Object.fromEntries(
    groups.flatMap((group) =>
      group.tiles.map((tile) => [tile.id, tile] as const),
    ),
  );
  const requests = Object.values(recordsByTileID).flatMap((record) => {
    const view =
      record.editorState?.displayView ??
      (record.source.kind === 'view' ? record.source.view : null);
    const seriesTile = seriesTilesByID[record.tileID];
    return view &&
      (record.source.kind === 'series'
        ? !!seriesTile
        : record.selectionOverride === true)
      ? [
          {
            id: record.tileID,
            recordID: record.recordID,
            seriesName: record.seriesName,
            kind: record.source.kind,
            offsetMm: record.offsetMm,
            order: seriesTile?.order ?? 0,
            thicknessMm:
              record.source.kind === 'series'
                ? groups.find((group) => group.seriesName === record.seriesName)
                    ?.slabThicknessMm
                : undefined,
            view,
          },
        ]
      : [];
  });
  const key = `${toothID}:${JSON.stringify(requests)}`;
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const cacheRef = useRef(getFullHeadMprSavedViewTiles(toothID));
  const [rendered, setRendered] = useState<{
    key: string;
    loading: LoadingStateType;
  }>({ key: '', loading: 'idle' });

  useEffect(() => {
    const requests = requestsRef.current;
    cacheRef.current = getFullHeadMprSavedViewTiles(toothID);
    if (!isEnabled || !requests.length) {
      cacheRef.current = {};
      return;
    }
    cacheRef.current = Object.fromEntries(
      Object.entries(cacheRef.current).filter(([tileID]) =>
        requests.some((request) => request.id === tileID),
      ),
    );
    const controller = new AbortController();
    setRendered({ key, loading: 'pending' });
    const render = async () => {
      await requests.reduce<Promise<void>>(async (previous, request) => {
        await previous;
        controller.signal.throwIfAborted();
        const requestKey = makeRasterKey({ toothID, ...request });
        const cached = cacheRef.current[request.id];
        if (cached?.key === requestKey) {
          return;
        }
        const scale =
          Math.max(request.view.widthMm, request.view.heightMm) /
          FULL_HEAD_MPR_SAVED_VIEW_PREVIEW_SIDE_PX;
        const raster = await renderFullHeadMPRView({
          heightPx: Math.max(1, Math.round(request.view.heightMm / scale)),
          signal: controller.signal,
          thicknessMm: request.thicknessMm,
          view: request.view,
          widthPx: Math.max(1, Math.round(request.view.widthMm / scale)),
        });
        controller.signal.throwIfAborted();
        cacheRef.current = {
          ...cacheRef.current,
          [request.id]: {
            key: requestKey,
            pngUrl: pixelsToPngUrl(raster),
            view: raster.view,
          },
        };
        putFullHeadMprSavedViewTiles(toothID, cacheRef.current);
      }, Promise.resolve());
      if (!controller.signal.aborted) {
        setRendered({ key, loading: 'succeeded' });
      }
    };
    void render().catch((error: unknown) => {
      if (!controller.signal.aborted) {
        debug(
          `[FullHeadMPR] event=saved_view_render_failed message=${error instanceof Error ? error.message : String(error)}`,
        );
        setRendered({ key, loading: 'failed' });
      }
    });
    return () => controller.abort();
  }, [key, isEnabled, toothID]);

  const cachedTiles = requests.flatMap<FullHeadMprSliceTileView>(
    (request, order) => {
      const cached = cacheRef.current[request.id];
      const requestKey = makeRasterKey({ toothID, ...request });
      if (cached?.key !== requestKey) {
        return [];
      }
      const tile = {
        id: request.id,
        offsetMm: request.offsetMm,
        order: request.kind === 'series' ? request.order : order,
        pngUrl: cached.pngUrl,
        seriesName: request.seriesName,
        displayView: cached.view,
      };
      return [
        request.kind === 'series'
          ? { ...tile, kind: 'series' }
          : {
              ...tile,
              kind: 'view',
              recordID: request.recordID,
              toothID,
              view: cached.view,
            },
      ];
    },
  );
  const savedTiles = isEnabled ? cachedTiles : [];
  const savedTilesByID = Object.fromEntries(
    savedTiles.map((tile) => [tile.id, tile] as const),
  );
  const renderedGroups = groups.map((group) => ({
    ...group,
    tiles: group.tiles.map((tile) => savedTilesByID[tile.id] ?? tile),
  }));
  return {
    groups: renderedGroups,
    hasSavedViews: requests.length > 0,
    savedViewsLoading:
      !requests.length || savedTiles.length === requests.length
        ? ('succeeded' as const)
        : rendered.key === key
          ? rendered.loading
          : ('pending' as const),
    tiles: [
      ...renderedGroups.flatMap((group) => group.tiles),
      ...savedTiles.filter((tile) => tile.kind === 'view'),
    ],
  };
};
